import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { orcamentoFornecedorService } from "../services/orcamentoFornecedorService";
import { avisarCotacaoEditada } from "../utils/avisoCotacaoEditada";
import { HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { useFeedback } from "@contexts/FeedbackContext";
import { useOrcamentoFornecedor } from "../hooks/useOrcamentoFornecedor";
import { formatCodigoOrcamento } from "../utils/orcamento-formatters";
import { OrcamentoHeader } from "../components/orcamento-fornecedor/OrcamentoHeader";
import { OrcamentoProgress } from "../components/orcamento-fornecedor/OrcamentoProgress";
import { OrcamentoNavigation } from "../components/orcamento-fornecedor/OrcamentoNavigation";
import { Etapa1Identificacao } from "../components/orcamento-fornecedor/etapas/Etapa1Identificacao";
import { Etapa2Valores } from "../components/orcamento-fornecedor/etapas/Etapa2Valores";
import { Etapa3Assinatura } from "../components/orcamento-fornecedor/etapas/Etapa3Assinatura";
import { Etapa4Confirmacao } from "../components/orcamento-fornecedor/etapas/Etapa4Confirmacao";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";
import { escolherCorte, type FaixaInteira } from "../utils/pdfPaginacao";
import { prepararImagensParaPdf } from "../utils/pdf-imagens";

export const PreenchimentoOrcamento: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Edição interna: a equipe corrigindo a cotação pelo mesmo portal. Definido
  // durante a renderização, e não num efeito, porque o carregamento do hook
  // abaixo já precisa sair com `?edicao=interna`. A API confere o login.
  const pediuEdicaoInterna = searchParams.get("edicao") === "interna";
  orcamentoFornecedorService.definirEdicaoInterna(pediuEdicaoInterna);
  React.useEffect(
    () => () => orcamentoFornecedorService.definirEdicaoInterna(false),
    [],
  );

  const { confirm, showSuccess, showError } = useFeedback();
  const [attemptedNext, setAttemptedNext] = React.useState(false);
  const [pdfHtmlContent, setPdfHtmlContent] = React.useState<any>(null);
  const [maxStepReached, setMaxStepReached] = React.useState(1);

  // Validar token
  if (!token) {
    return (
      <div className="min-h-screen container flex items-center justify-center p-4">
        <div className="alert alert-error max-w-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">Token inválido</h3>
            <div className="text-sm">
              O link de acesso ao orçamento é inválido.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const {
    dadosOrcamento,
    etapaAtual,
    loading,
    saving,
    error,
    dadosEtapa1,
    dadosEtapa2,
    dadosEtapa3,
    dadosEtapa4,
    atualizarDadosEtapa1,
    atualizarDadosEtapa2,
    atualizarDadosEtapa3,
    atualizarDadosEtapa4,
    irParaEtapa,
    proximaEtapa,
    etapaAnterior,
    validarEtapaAtual,
    finalizarOrcamento,
  } = useOrcamentoFornecedor(token);

  const edicaoInterna = !!dadosOrcamento?.edicaoInterna;

  // Atualizar maxStepReached quando etapaAtual mudar. Na edição interna todas
  // as etapas já foram preenchidas, então todas ficam navegáveis.
  React.useEffect(() => {
    setMaxStepReached((prev) => Math.max(prev, edicaoInterna ? 4 : etapaAtual));
  }, [etapaAtual, edicaoInterna]);

  // Reset attemptedNext quando mudar de etapa
  React.useEffect(() => {
    setAttemptedNext(false);
  }, [etapaAtual]);

  // Validação da etapa atual
  const validacao = validarEtapaAtual();

  // Handler para próxima etapa
  const handleProxima = async () => {
    setAttemptedNext(true);
    if (validacao.valid) {
      try {
        await proximaEtapa();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err: any) {
        showError(
          err.message || "Erro ao salvar dados. Por favor, tente novamente.",
        );
      }
    } else {
      const mensagemErro =
        etapaAtual === 2 && validacao.errors.itens
          ? "Preencha o valor unitário de todos os itens destacados em vermelho."
          : "Por favor, corrija os campos destacados em vermelho antes de continuar.";
      showError(mensagemErro);

      // Scroll até o primeiro campo com erro
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  // Handler para etapa anterior
  const handleAnterior = () => {
    etapaAnterior();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  async function criarPDF() {
    if (!pdfHtmlContent?.current) return;

    // Guardado fora do try porque quem restaura é o finally.
    let estiloOriginal: {
      width: string;
      minWidth: string;
      maxWidth: string;
    } | null = null;

    try {
      showSuccess("Gerando PDF... Aguarde alguns segundos.");

      const PIXEL_RATIO = 2;
      const element = pdfHtmlContent.current;

      // Largura de uma folha A4 a 96dpi (210mm). O documento é capturado sempre
      // nesta medida, independentemente da tela de quem preenche.
      //
      // Sem isto o PDF saía desproporcional: a captura tem a largura que o
      // documento tiver NA TELA, e logo abaixo ela é esticada até a largura do
      // A4. Numa janela larga o documento ocupa ~800px e a conta fecha; numa
      // janela estreita ele encolhe para ~360px, e essa captura pequena era
      // AMPLIADA até o A4 — daí o texto gigante e o corte nas laterais. Valia
      // para celular e para notebook com a janela reduzida, porque o que pesa é
      // a largura da janela, não o aparelho.
      //
      // Forçar a largura resolve porque o documento não tem nenhuma regra
      // responsiva: ele só se estica ou encolhe conforme o espaço.
      const LARGURA_A4_PX = 794;
      estiloOriginal = {
        width: element.style.width,
        minWidth: element.style.minWidth,
        maxWidth: element.style.maxWidth,
      };

      // Assinatura e logo chegam como URL do Blob Storage. Converter e garantir
      // que estão decodificadas ANTES da captura — o html-to-image descarta em
      // silêncio qualquer imagem que ainda não terminou de carregar.
      const { falhas } = await prepararImagensParaPdf(element);

      if (falhas.length > 0) {
        // Interromper de propósito: um orçamento sem assinatura ou sem logo não
        // pode ser enviado como se estivesse completo.
        throw new Error(
          `Não foi possível carregar ${falhas.join(", ")} para o PDF. ` +
            "Verifique sua conexão e tente enviar novamente.",
        );
      }

      element.style.width = `${LARGURA_A4_PX}px`;
      element.style.minWidth = `${LARGURA_A4_PX}px`;
      element.style.maxWidth = `${LARGURA_A4_PX}px`;

      // Dois quadros: um para o navegador aplicar a largura, outro para o texto
      // refluir. Medir antes disso devolveria as posições do layout antigo.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

      // Capturar posições de quebra de página no DOM antes de renderizar.
      // html-to-image captura CSS de tela (não @media print), então os
      // estilos page-break-before precisam ser detectados via posição DOM.
      //
      // A medição vem DEPOIS de fixar a largura de propósito: o texto reflui e
      // as quebras mudam de lugar junto.
      const containerRect = element.getBoundingClientRect();
      const pageBreakEls = Array.from(
        element.querySelectorAll(".company-info-block"),
      ) as HTMLElement[];
      const screenBreakPositions: number[] = [];

      pageBreakEls.forEach((el: HTMLElement) => {
        const relTop = el.getBoundingClientRect().top - containerRect.top;
        if (relTop > 0) screenBreakPositions.push(relTop);
      });

      // Blocos que não podem ser partidos ao meio: os marcados no documento e
      // toda linha de tabela, que inclui a do TOTAL GERAL. Medidos aqui, junto
      // com as quebras, depois de a largura estar fixada. Ver escolherCorte.
      const faixaDe = (topoEl: Element, baseEl: Element = topoEl): FaixaInteira => ({
        topo: Math.round(
          (topoEl.getBoundingClientRect().top - containerRect.top) * PIXEL_RATIO,
        ),
        base: Math.round(
          (baseEl.getBoundingClientRect().bottom - containerRect.top) * PIXEL_RATIO,
        ),
      });
      const faixasInteiras: FaixaInteira[] = Array.from(
        element.querySelectorAll(".page-break-inside-avoid, tr") as NodeListOf<Element>,
      )
        // O bloco que envolve a tabela também é marcado, mas mantê-lo inteiro
        // empurrava a tabela toda para a página 2 e deixava a primeira quase
        // vazia. A tabela quebra entre linhas.
        .filter((el) => !el.querySelector("table"))
        .map((el) => faixaDe(el));

      // TOTAL GERAL preso à última linha de item: sozinho no topo da página
      // seguinte ele perde a referência do que está somando.
      (Array.from(element.querySelectorAll("table")) as HTMLTableElement[]).forEach(
        (tabela) => {
          const ultimaLinha = tabela.querySelector("tbody tr:last-child");
          const total = tabela.querySelector("tfoot");
          if (ultimaLinha && total) faixasInteiras.push(faixaDe(ultimaLinha, total));
        },
      );

      // Capturar div como PNG usando html-to-image (ignora parsing de CSS)
      const opcoesCaptura = {
        quality: 1,
        pixelRatio: PIXEL_RATIO,
        backgroundColor: "#ffffff",
        filter: (node: HTMLElement) => {
          if (node.classList?.contains("no-print")) {
            return false;
          }
          return true;
        },
      };

      // O html-to-image tem um problema conhecido de descartar imagens e fontes
      // na primeira renderização (o clone é montado antes de tudo estar pronto).
      // Renderizar uma vez e descartar torna a captura seguinte confiável.
      await htmlToImage.toPng(element, opcoesCaptura);
      const dataUrl = await htmlToImage.toPng(element, opcoesCaptura);

      // Criar imagem a partir do dataUrl
      const img = new Image();
      img.src = dataUrl;

      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Criar PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const usableHeight = pdfHeight - margin * 2;

      // Escala de pixels de imagem para pontos PDF
      const scale = pdfWidth / img.width;

      // Converter posições de tela (CSS px) para pixels de imagem
      const imgBreakPositions = screenBreakPositions.map((p) =>
        Math.round(p * PIXEL_RATIO),
      );

      // Construir seções lógicas: cada quebra de página gera uma nova seção
      const sectionStarts = [0, ...imgBreakPositions];
      const sectionEnds = [...imgBreakPositions, img.height];

      let firstPage = true;

      for (let i = 0; i < sectionStarts.length; i++) {
        const sectionStartPx = sectionStarts[i];
        const sectionEndPx = sectionEnds[i];
        const sectionHeightPx = sectionEndPx - sectionStartPx;
        const sectionHeightPdf = sectionHeightPx * scale;

        if (sectionHeightPdf <= usableHeight) {
          // Seção cabe em uma página
          if (!firstPage) pdf.addPage();
          firstPage = false;

          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = sectionHeightPx;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(
              img,
              0,
              sectionStartPx,
              img.width,
              sectionHeightPx,
              0,
              0,
              img.width,
              sectionHeightPx,
            );
            const pageDataUrl = canvas.toDataURL("image/png");
            pdf.addImage(
              pageDataUrl,
              "PNG",
              0,
              margin,
              pdfWidth,
              sectionHeightPdf,
              undefined,
              "MEDIUM",
            );
          }
        } else {
          // Seção maior que uma página: cada página termina no último ponto
          // que não parte um bloco ao meio, e o bloco seguinte abre a próxima.
          let currentPx = sectionStartPx;
          const capacidadePx = Math.floor(usableHeight / scale);

          while (currentPx < sectionEndPx) {
            if (!firstPage) pdf.addPage();
            firstPage = false;

            const limite = Math.min(sectionEndPx, currentPx + capacidadePx);
            const corte =
              limite >= sectionEndPx
                ? sectionEndPx
                : escolherCorte(currentPx, limite, faixasInteiras);
            const pageHeightPx = corte - currentPx;
            const pageHeightPdf = pageHeightPx * scale;

            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = pageHeightPx;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(
                img,
                0,
                currentPx,
                img.width,
                pageHeightPx,
                0,
                0,
                img.width,
                pageHeightPx,
              );
              const pageDataUrl = canvas.toDataURL("image/png");
              pdf.addImage(
                pageDataUrl,
                "PNG",
                0,
                margin,
                pdfWidth,
                pageHeightPdf,
                undefined,
                "MEDIUM",
              );
            }

            currentPx = corte;
          }
        }
      }

      // Aguardar processamento do PDF antes de gerar base64
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Retornar como base64
      const pdfBase64 = pdf.output("datauristring");
      return pdfBase64;
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      showError("Erro ao gerar PDF. Tente novamente.");
      throw error;
    } finally {
      // A largura fixa é só para a captura. Em finally porque, se a geração
      // falhar no meio, o fornecedor não pode ficar com o documento travado
      // numa largura maior que a tela dele.
      const element = pdfHtmlContent?.current;
      if (element && estiloOriginal) {
        element.style.width = estiloOriginal.width;
        element.style.minWidth = estiloOriginal.minWidth;
        element.style.maxWidth = estiloOriginal.maxWidth;
      }
    }
  }

  // Handler para finalizar e enviar
  const handleFinalizar = async () => {
    if (!validacao.valid) {
      showError("Por favor, aceite os termos antes de enviar.");
      return;
    }

    const confirmed = await confirm(
      edicaoInterna
        ? {
            title: "Salvar a cotação editada?",
            message:
              "O PDF é gerado de novo e passa a ser o principal; o anterior fica registrado no histórico da planilha. O status da cotação não muda.",
            tone: "default",
          }
        : {
            title: "Deseja enviar este orçamento?",
            message: "Após o envio, não será mais possível editar as informações.",
            tone: "default",
          },
    );

    if (!confirmed) return;

    try {
      const pdf = await criarPDF();
      // Enviar HTML para geração de PDF junto com finalização
      await finalizarOrcamento(pdf);

      if (edicaoInterna) {
        const planilhaId = dadosOrcamento!.planilha.id;
        // A planilha aberta na outra aba atualiza as linhas na hora.
        avisarCotacaoEditada({ planilhaId, orcamentoId: dadosOrcamento!.id });
        showSuccess("Cotação atualizada. A planilha já mostra os valores novos.");

        // Aberta pelo menu da planilha, esta aba pode se fechar e devolver o
        // usuário para lá. Aberta de outro jeito, o navegador não deixa
        // fechar — aí vai para a planilha nesta mesma aba.
        setTimeout(() => {
          window.close();
          setTimeout(() => {
            if (!window.closed) navigate(`/planilhas/editar/${planilhaId}`);
          }, 300);
        }, 1500);
        return;
      }

      showSuccess("Orçamento enviado com sucesso!");

      // Recarregar a página para mostrar mensagem de concluído
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      showError("Erro ao enviar orçamento. Por favor, tente novamente.");
      console.error("Erro ao finalizar:", err);
    }
  };

  // Estados de loading e erro
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg"></span>
          <p className="mt-4 text-lg">Carregando orçamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="alert alert-error max-w-lg">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">Erro ao carregar orçamento</h3>
            <div className="text-sm">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (
    !dadosOrcamento ||
    !dadosOrcamento.planilha ||
    !dadosOrcamento.fornecedor
  ) {
    return null;
  }

  // Verificar se orçamento já foi rejeitado. As telas de rejeitado e concluído
  // são para o fornecedor; a equipe, na edição interna, entra no formulário.
  if (!edicaoInterna && dadosOrcamento.status === "rejeitado") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-red-500 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <svg
                  className="h-10 w-10 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Orçamento Rejeitado
              </h2>
              <p className="text-gray-600 mb-6">
                Este orçamento foi rejeitado anteriormente. Não é mais possível
                preencher ou editar as informações.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">
                  <strong>Código do Orçamento:</strong>{" "}
                  {formatCodigoOrcamento(dadosOrcamento.codigoSequencial)}
                </p>
                <p className="text-sm text-red-800 mt-1">
                  <strong>Fornecedor:</strong>{" "}
                  {dadosOrcamento.fornecedor.nome ||
                    dadosOrcamento.fornecedor.razaoSocial}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Em caso de dúvidas, entre em contato com a Monumenta.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Verificar se orçamento já foi concluído
  if (!edicaoInterna && dadosOrcamento.status === "concluido") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-lg shadow-lg border-2 border-green-500 p-8">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Orçamento Enviado!
              </h2>
              <p className="text-gray-600 mb-6">
                Este orçamento já foi finalizado e enviado com sucesso. Não é
                mais possível fazer alterações ou visualizar os detalhes.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Código do Orçamento:</strong>{" "}
                  {formatCodigoOrcamento(dadosOrcamento.codigoSequencial)}
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  <strong>Fornecedor:</strong>{" "}
                  {dadosOrcamento.fornecedor.nome ||
                    dadosOrcamento.fornecedor.razaoSocial}
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Em caso de dúvidas, entre em contato com a Monumenta.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {edicaoInterna && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 text-sm px-4 py-3">
          <div className="container mx-auto max-w-5xl">
            <strong>Edição interna da cotação.</strong> Você está alterando a
            proposta de {dadosOrcamento.fornecedor.razaoSocial ||
              dadosOrcamento.fornecedor.nome}{" "}
            em nome da Monumenta. Ao salvar na última etapa, o PDF é gerado de
            novo e substitui o atual; o status da cotação não muda.
          </div>
        </div>
      )}

      {/* Header */}
      <OrcamentoHeader
        codigoOrcamento={formatCodigoOrcamento(dadosOrcamento.codigoSequencial)}
        nomeEvento={dadosOrcamento.planilha.evento}
        nomeCliente={dadosOrcamento.planilha.cliente.nome}
        dataInicio={dadosOrcamento.planilha.dataInicio}
        dataFim={dadosOrcamento.planilha.dataFim}
        localEvento={dadosOrcamento.planilha.localEvento}
        ufEvento={dadosOrcamento.planilha.ufEvento}
        produtores={dadosOrcamento.produtores}
      />

      {/* Progress */}
      <OrcamentoProgress
        currentStep={etapaAtual}
        maxStepReached={maxStepReached}
        onStepClick={(etapa) => {
          if (etapa <= maxStepReached) {
            irParaEtapa(etapa);
          }
        }}
      />

      {/* Container Principal */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Conteúdo da Etapa */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
          <div className="flex justify-between items-center gap-4 pb-5">
            <button
              type="button"
              onClick={handleAnterior}
              disabled={saving || etapaAtual === 1}
              className="btn btn-outline"
            >
              <HiArrowLeft className="h-4 w-4 mr-1" />
              Voltar
            </button>
            <span className="text-sm text-gray-500 font-medium">
              Etapa {etapaAtual} de 4
            </span>
            {etapaAtual < 4 ? (
              <button
                type="button"
                onClick={handleProxima}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <>
                    Próximo
                    <HiArrowRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </button>
            ) : (
              <div className="w-24" />
            )}
          </div>

          {etapaAtual === 1 && (
            <Etapa1Identificacao
              dadosOrcamento={dadosOrcamento}
              dadosIdentificacao={dadosEtapa1}
              onDadosChange={atualizarDadosEtapa1}
              errors={validacao.errors}
              showErrors={attemptedNext}
            />
          )}

          {etapaAtual === 2 && (
            <Etapa2Valores
              itens={dadosOrcamento.itens}
              dadosValores={dadosEtapa2}
              onDadosChange={atualizarDadosEtapa2}
              errors={validacao.errors}
              showErrors={attemptedNext}
            />
          )}

          {etapaAtual === 3 && (
            <Etapa3Assinatura
              dadosAssinatura={dadosEtapa3}
              onDadosChange={atualizarDadosEtapa3}
              errors={validacao.errors}
              showErrors={attemptedNext}
            />
          )}

          {etapaAtual === 4 && (
            <Etapa4Confirmacao
              dadosOrcamento={dadosOrcamento}
              dadosIdentificacao={dadosEtapa1}
              dadosValores={dadosEtapa2}
              dadosAssinatura={dadosEtapa3}
              aceites={dadosEtapa4}
              onAceiteChange={(termo, valor) => {
                atualizarDadosEtapa4({ [termo]: valor });
              }}
              errors={validacao.errors}
              token={token}
              onPdfContentReady={setPdfHtmlContent}
              onFinalizar={handleFinalizar}
              isLoading={saving}
            />
          )}

          {/* Navegação - Oculta na etapa 4 pois o botão de envio está dentro da etapa */}
          {etapaAtual !== 4 && (
            <OrcamentoNavigation
              etapaAtual={etapaAtual}
              totalEtapas={4}
              onProxima={handleProxima}
              onAnterior={handleAnterior}
              onFinalizar={handleFinalizar}
              isLoading={saving}
              isEtapaValida={validacao.valid}
            />
          )}

          {/* Botão Voltar na Etapa 4 */}
          {etapaAtual === 4 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleAnterior}
                disabled={saving}
                className="btn btn-outline"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Voltar
              </button>
            </div>
          )}
        </div>

        {/* Footer informativo */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Sistema de Cotação da Monumenta - Todos os direitos reservados</p>
          <p className="mt-1">Em caso de dúvidas, entre em contato conosco</p>
        </div>
      </div>
    </div>
  );
};
